import { useEffect, useId, useMemo, useRef, useState } from 'react'
import { DayPicker, getDefaultClassNames, type DateRange as RdpRange } from 'react-day-picker'
import 'react-day-picker/style.css'
import { format, parse, subDays, subWeeks, subMonths, addDays, startOfDay, isValid } from 'date-fns'
import { formatInTimeZone, fromZonedTime } from 'date-fns-tz'
import { Calendar as CalendarIcon, ChevronDown, ChevronLeft, ChevronRight, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { DEFAULT_EVENT_TIMEZONE } from '@/lib/eventTime'

/**
 * Shared date-range filter — the Finix-style operator + calendar picker that
 * replaces the studio's scattered raw `datetime-local` pairs.
 *
 * Contract: emits `{ gte, lte }` as EVENT-LOCAL 'yyyy-MM-ddTHH:mm' strings —
 * exactly the shape the existing From/To inputs produced — so a parent keeps
 * converting with `eventLocalToServerNaive(value, event.timezone)` unchanged.
 * The Event-time / UTC toggle only changes how the picked wall-clock is
 * interpreted; the emitted value is always event-local, so the round-trip
 * through the parent's conversion lands on the intended instant either way.
 */
export interface DateFilterValue {
  /** event-local 'yyyy-MM-ddTHH:mm', or undefined for an open bound */
  gte?: string
  lte?: string
}

type Operator = 'in_last' | 'equal' | 'between' | 'on_after' | 'before_on'
type Zone = 'event' | 'utc'
type Unit = 'days' | 'weeks' | 'months'

const OPERATOR_LABELS: Record<Operator, string> = {
  in_last: 'is in the last',
  equal: 'is equal to',
  between: 'is between',
  on_after: 'is on or after',
  before_on: 'is before or on',
}

const LOCAL_FMT = "yyyy-MM-dd'T'HH:mm"

/** A picked calendar day + a wall-clock time, interpreted in `zone`, expressed
 * as an event-local 'yyyy-MM-ddTHH:mm' string. When zone is the event's own,
 * the wall-clock already IS event-local; for UTC we shift to the instant then
 * re-read it in the event's zone. Date-granularity callers get 'yyyy-MM-dd'. */
function toEventLocal(day: Date, time: string, zone: Zone, eventTz: string, dateOnly: boolean): string {
  const dayStr = format(day, 'yyyy-MM-dd')
  if (dateOnly) return dayStr
  const naive = `${dayStr}T${time}`
  if (zone === 'event') return naive.slice(0, 16)
  return formatInTimeZone(fromZonedTime(naive, 'UTC'), eventTz, LOCAL_FMT)
}

function summarize(value: DateFilterValue): string | null {
  const fmt = (s: string) => {
    // Accept both 'yyyy-MM-ddTHH:mm' and date-only 'yyyy-MM-dd'.
    const d = parse(s.slice(0, 10), 'yyyy-MM-dd', new Date())
    return isValid(d) ? format(d, 'MMM d, yyyy') : s
  }
  if (value.gte && value.lte) {
    if (value.gte.slice(0, 10) === value.lte.slice(0, 10)) return fmt(value.gte)
    return `${fmt(value.gte)} – ${fmt(value.lte)}`
  }
  if (value.gte) return `On or after ${fmt(value.gte)}`
  if (value.lte) return `Before ${fmt(value.lte)}`
  return null
}

function DateChip({ date, placeholder }: { date?: Date; placeholder: string }) {
  return (
    <span className={cn(
      'w-28 whitespace-nowrap rounded-md border px-2 py-1.5 text-center text-xs font-medium',
      date
        ? 'border-blue-300 bg-blue-50 text-blue-700 dark:border-blue-800 dark:bg-blue-950/40 dark:text-blue-300'
        : 'border-neutral-200 text-neutral-400 dark:border-neutral-700 dark:text-neutral-500',
    )}>
      {date ? format(date, 'MMM d, yyyy') : placeholder}
    </span>
  )
}

interface DateFilterProps {
  value: DateFilterValue
  onChange: (v: DateFilterValue) => void
  /** Event IANA timezone; picked/relative dates resolve against this. */
  eventTimeZone?: string
  /** 'minute' (default) emits 'yyyy-MM-ddTHH:mm', shows time inputs + a tz
   * toggle; 'date' emits 'yyyy-MM-dd' and hides both (for date-only filters). */
  granularity?: 'minute' | 'date'
  /** Month the calendar opens on when nothing is selected — pass the event
   * date so it lands on the event's month, not today's. */
  defaultMonth?: Date
  label?: string
  className?: string
}

// Keep react-day-picker's DEFAULT layout classes (its `.rdp-nav` is
// position:absolute — overriding it makes the < > arrows float). Theme only
// the colors, via v10's CSS variables, to EventPay blue. Text color comes
// from the wrapper className so both light/dark themes inherit correctly.
const CALENDAR_STYLE = {
  '--rdp-accent-color': '#3b82f6', // blue-500 (selected day) — matches active nav
  '--rdp-accent-background-color': '#dbeafe', // blue-100
  '--rdp-range_start-background': '#3b82f6',
  '--rdp-range_end-background': '#3b82f6',
  '--rdp-range_middle-background-color': '#dbeafe', // blue-100
  '--rdp-range_middle-color': '#1d4ed8', // blue-700
  '--rdp-today-color': '#2563eb', // blue-600
  // Tighten the default (which renders tall, loose rows).
  '--rdp-day-width': '2.1rem',
  '--rdp-day-height': '2.1rem',
  '--rdp-day_button-width': '2.1rem',
  '--rdp-day_button-height': '2.1rem',
  '--rdp-weekday-padding': '0.25rem 0',
  '--rdp-nav_button-width': '1.75rem',
  '--rdp-nav_button-height': '1.75rem',
} as React.CSSProperties
// select-none stops the caption text from showing a selection highlight.
const CALENDAR_CLASS = 'mx-auto select-none text-sm text-neutral-800 dark:text-neutral-200'
// Small, neutral nav arrows (react-day-picker's default chevrons are large).
const CALENDAR_COMPONENTS = {
  Chevron: ({ orientation }: { orientation?: 'up' | 'down' | 'left' | 'right' }) =>
    orientation === 'left'
      ? <ChevronLeft className="h-4 w-4 text-neutral-500 dark:text-neutral-400" />
      : <ChevronRight className="h-4 w-4 text-neutral-500 dark:text-neutral-400" />,
}
// Round the range endpoints (start rounds left, end rounds right, middle
// stays square to form the connecting bar; a lone selected day is a full
// pill). Both sides are set explicitly because range endpoints also carry
// the `selected` class — otherwise the two rules would fight.
const dcn = getDefaultClassNames()
const CALENDAR_RANGE_CLASSNAMES = {
  // !text-sm overrides react-day-picker's `.rdp-selected { font-size: large }`
  // so selected days aren't larger than the rest. Every range day (start,
  // middle, end) also carries `selected`, so this reaches all of them.
  selected: `${dcn.selected} !rounded-full !text-sm`,
  range_start: `${dcn.range_start} !rounded-l-full !rounded-r-none`,
  range_end: `${dcn.range_end} !rounded-r-full !rounded-l-none`,
  range_middle: `${dcn.range_middle} !rounded-none`,
}

export function DateFilter({ value, onChange, eventTimeZone, granularity = 'minute', defaultMonth, label = 'Date and time', className }: DateFilterProps) {
  const tz = eventTimeZone || DEFAULT_EVENT_TIMEZONE
  const dateOnly = granularity === 'date'
  const [open, setOpen] = useState(false)
  const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({})
  const buttonRef = useRef<HTMLButtonElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  // Determine alignment dynamically when opened
  useEffect(() => {
    if (open && containerRef.current) {
      const containerRect = containerRef.current.getBoundingClientRect()
      const popupWidth = 320 // matches w-[320px]
      
      let idealLeft = containerRect.left
      
      // Prevent overflowing right edge of screen
      if (idealLeft + popupWidth > window.innerWidth - 16) {
        idealLeft = window.innerWidth - popupWidth - 16
      }
      
      // Prevent overflowing left edge of screen
      if (idealLeft < 16) {
        idealLeft = 16
      }
      
      // Calculate relative offset from container
      setDropdownStyle({
        left: `${idealLeft - containerRect.left}px`,
      })
    }
  }, [open])

  // Unique per instance so two filters on one page don't share a radio group.
  const tzGroupName = useId()

  // Draft state (only committed to onChange on Apply)
  const [operator, setOperator] = useState<Operator>('between')
  const [zone, setZone] = useState<Zone>('event')
  const [lastN, setLastN] = useState('7')
  const [unit, setUnit] = useState<Unit>('days')
  const [single, setSingle] = useState<Date | undefined>(undefined)
  const [range, setRange] = useState<RdpRange | undefined>(undefined)
  // Minute-granularity time-of-day bounds (HH:mm, no seconds). Hidden behind
  // an "Include time" toggle; when off, filters cover the whole day exactly
  // as before.
  const [includeTime, setIncludeTime] = useState(false)
  const [startTime, setStartTime] = useState('00:00')
  const [endTime, setEndTime] = useState('23:59')
  // The value WE last emitted. If the current value still equals it, the
  // draft already holds the operator the user applied, so re-opening must
  // NOT re-seed (the gte/lte heuristic can't distinguish 'in the last' from
  // 'between' and would clobber the choice). A value that differs (e.g. set
  // externally by a drill-down) DOES re-seed.
  const lastEmitted = useRef<DateFilterValue>({})
  // The exact draft that produced `lastEmitted`. Restored on re-open so the
  // operator/times survive (the gte/lte heuristic can't recover them) AND
  // any uncommitted edits from an outside-click close are discarded.
  type Draft = {
    operator: Operator; zone: Zone; lastN: string; unit: Unit
    single?: Date; range?: RdpRange; includeTime: boolean; startTime: string; endTime: string
  }
  const appliedDraft = useRef<Draft | null>(null)

  // Seed the draft from the current value when the picker OPENS. Called from
  // the trigger handler, deliberately NOT a useEffect: synchronous setState
  // inside an effect body trips react-hooks/set-state-in-effect and forces an
  // extra render. Setting state in an event handler is the idiomatic path.
  const seedDraft = () => {
    // Empty value (cleared here OR externally, e.g. a drill-down chip's ×) →
    // reset to a clean slate. FIRST so stale selections can't be resurrected.
    if (!value.gte && !value.lte) {
      setOperator('between'); setRange(undefined); setSingle(undefined)
      setIncludeTime(false); setStartTime('00:00'); setEndTime('23:59')
      return
    }
    // Our own last-applied value → restore the exact draft we applied.
    if (value.gte === lastEmitted.current.gte && value.lte === lastEmitted.current.lte) {
      const a = appliedDraft.current
      if (a) {
        setOperator(a.operator); setZone(a.zone); setLastN(a.lastN); setUnit(a.unit)
        setSingle(a.single); setRange(a.range)
        setIncludeTime(a.includeTime); setStartTime(a.startTime); setEndTime(a.endTime)
      }
      return
    }
    const toDate = (s?: string) => (s ? parse(s.slice(0, 10), 'yyyy-MM-dd', new Date()) : undefined)
    const timeOf = (s?: string) => (s && s.length >= 16 ? s.slice(11, 16) : undefined)
    if (value.gte && value.lte) {
      setOperator('between')
      setRange({ from: toDate(value.gte), to: toDate(value.lte) })
    } else if (value.gte) {
      setOperator('on_after')
      setSingle(toDate(value.gte))
    } else if (value.lte) {
      setOperator('before_on')
      setSingle(toDate(value.lte))
    }
    if (!dateOnly) {
      const gT = timeOf(value.gte)
      const lT = timeOf(value.lte)
      if (gT) setStartTime(gT)
      if (lT) setEndTime(lT)
      // A value carrying non-whole-day times means time was included.
      if ((gT && gT !== '00:00') || (lT && lT !== '23:59')) setIncludeTime(true)
    }
  }

  const toggleOpen = () => {
    if (open) { setOpen(false); return }
    seedDraft()
    setOpen(true)
  }

  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [open])

  const summary = useMemo(() => summarize(value), [value])
  const hasValue = !!(value.gte || value.lte)

  const apply = () => {
    let next: DateFilterValue = {}
    const emitFmt = dateOnly ? 'yyyy-MM-dd' : LOCAL_FMT
    if (operator === 'in_last') {
      const n = Math.max(1, parseInt(lastN, 10) || 1)
      const now = new Date()
      const base =
        unit === 'weeks' ? subWeeks(now, n)
          : unit === 'months' ? subMonths(now, n)
            : subDays(now, n)
      // Start of the day AFTER `base` → the window is exactly N days ending
      // today (inclusive), not N+1. "Last 7 days" = today + the 6 prior.
      next = {
        gte: formatInTimeZone(startOfDay(addDays(base, 1)), tz, emitFmt),
        lte: formatInTimeZone(now, tz, emitFmt),
      }
    } else {
      // Minute granularity uses the chosen time-of-day; date-only ignores it
      // (toEventLocal drops the time). Default 00:00 / 23:59 = whole day.
      const useTimes = !dateOnly && includeTime
      const startClock = useTimes ? `${startTime}:00` : '00:00:00'
      const endClock = useTimes ? `${endTime}:00` : '23:59:59'
      if (operator === 'equal' && single) {
        next = { gte: toEventLocal(single, startClock, zone, tz, dateOnly), lte: toEventLocal(single, endClock, zone, tz, dateOnly) }
      } else if (operator === 'between' && range?.from) {
        const to = range.to ?? range.from
        next = { gte: toEventLocal(range.from, startClock, zone, tz, dateOnly), lte: toEventLocal(to, endClock, zone, tz, dateOnly) }
      } else if (operator === 'on_after' && single) {
        next = { gte: toEventLocal(single, startClock, zone, tz, dateOnly) }
      } else if (operator === 'before_on' && single) {
        next = { lte: toEventLocal(single, endClock, zone, tz, dateOnly) }
      }
    }
    lastEmitted.current = next
    appliedDraft.current = { operator, zone, lastN, unit, single, range, includeTime, startTime, endTime }
    onChange(next)
    setOpen(false)
  }

  const clear = () => {
    // Full reset so a later re-open starts clean, not on the old draft.
    setOperator('between'); setSingle(undefined); setRange(undefined)
    setIncludeTime(false); setStartTime('00:00'); setEndTime('23:59')
    setLastN('7'); setUnit('days')
    appliedDraft.current = null
    lastEmitted.current = {}
    onChange({})
    setOpen(false)
  }

  const calendarMode = operator === 'between' ? 'range' : 'single'

  return (
    <div ref={containerRef} className={cn('relative inline-block', className)}>
      <button
        ref={buttonRef}
        type="button"
        onClick={toggleOpen}
        className={cn(
          'inline-flex items-center gap-2 whitespace-nowrap rounded-md border px-4 py-2 text-sm font-medium shadow-sm transition-colors',
          hasValue
            ? 'border-blue-300 bg-blue-50 text-blue-700 dark:border-blue-800 dark:bg-blue-950/40 dark:text-blue-300'
            : 'border-neutral-300 text-neutral-600 hover:bg-neutral-50 dark:border-neutral-700 dark:text-neutral-300 dark:hover:bg-neutral-800/50',
        )}
      >
        <CalendarIcon className="w-4 h-4" />
        <span>{summary ?? label}</span>
        {hasValue ? (
          <X
            className="w-3.5 h-3.5 opacity-70 hover:opacity-100"
            onClick={(e) => { e.stopPropagation(); clear() }}
          />
        ) : (
          <ChevronDown className="w-3.5 h-3.5 opacity-70" />
        )}
      </button>

      {open && (
        <div 
          style={dropdownStyle}
          className="absolute z-[60] mt-2 w-[320px] max-w-[calc(100vw-2rem)] rounded-xl border border-neutral-200 bg-white p-4 shadow-xl dark:border-neutral-800 dark:bg-neutral-900"
        >
          <p className="mb-2 text-sm font-semibold text-neutral-900 dark:text-neutral-100">Filter by: {label.toLowerCase()}</p>

          <div className="relative mb-3">
            <select
              value={operator}
              onChange={(e) => setOperator(e.target.value as Operator)}
              className="w-full appearance-none rounded-lg border border-neutral-300 bg-white px-3 py-2 pr-8 text-sm font-medium text-neutral-900 dark:border-neutral-700 dark:bg-neutral-900 dark:text-neutral-100"
            >
              {(Object.keys(OPERATOR_LABELS) as Operator[]).map((op) => (
                <option key={op} value={op}>{OPERATOR_LABELS[op]}</option>
              ))}
            </select>
            <ChevronDown className="pointer-events-none absolute right-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400" />
          </div>

          {operator === 'in_last' ? (
            <div className="mb-3 flex items-center gap-2">
              <input
                type="number"
                min={1}
                value={lastN}
                onChange={(e) => setLastN(e.target.value)}
                className="w-24 rounded-lg border border-neutral-300 px-3 py-2 text-sm dark:border-neutral-700 dark:bg-neutral-900 dark:text-neutral-100"
              />
              <select
                value={unit}
                onChange={(e) => setUnit(e.target.value as Unit)}
                className="flex-1 rounded-lg border border-neutral-300 bg-white px-3 py-2 text-sm dark:border-neutral-700 dark:bg-neutral-900 dark:text-neutral-100"
              >
                <option value="days">days</option>
                <option value="weeks">weeks</option>
                <option value="months">months</option>
              </select>
            </div>
          ) : (
            <div className="mb-3 rounded-lg border border-neutral-200 p-2 dark:border-neutral-800">
              {/* Selected-date readout (Finix-style) — shows the picked
                  bound(s) as chips above the calendar. */}
              <div className="mb-2 flex items-center justify-center gap-2 text-sm">
                {calendarMode === 'range' ? (
                  <>
                    <DateChip date={range?.from} placeholder="Start" />
                    <span className="text-neutral-400 dark:text-neutral-500">and</span>
                    <DateChip date={range?.to} placeholder="End" />
                  </>
                ) : (
                  <DateChip date={single} placeholder="Select date" />
                )}
              </div>

              {/* Include time — hidden by default to keep the picker clean
                  (minute granularity only). */}
              {!dateOnly && (
                <div className="mb-2 space-y-2">
                  <label className="flex cursor-pointer items-center justify-center gap-2 text-xs font-medium text-neutral-600 dark:text-neutral-300">
                    <input type="checkbox" checked={includeTime} onChange={(e) => setIncludeTime(e.target.checked)}
                      className="h-4 w-4 rounded accent-blue-600" />
                    Include time
                  </label>
                  {includeTime && (
                    <div className="flex items-center justify-center gap-2 text-xs">
                      {operator !== 'before_on' && (
                        <input type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)}
                          className="w-28 rounded-md border border-neutral-300 px-2 py-1.5 text-neutral-900 dark:border-neutral-700 dark:bg-neutral-900 dark:text-neutral-100" />
                      )}
                      {(operator === 'between' || operator === 'equal') && <span className="text-neutral-400 dark:text-neutral-500">to</span>}
                      {operator !== 'on_after' && (
                        <input type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)}
                          className="w-28 rounded-md border border-neutral-300 px-2 py-1.5 text-neutral-900 dark:border-neutral-700 dark:bg-neutral-900 dark:text-neutral-100" />
                      )}
                    </div>
                  )}
                </div>
              )}

              <div className="flex justify-center">
                {calendarMode === 'range' ? (
                  <DayPicker mode="range" selected={range} onSelect={setRange} defaultMonth={defaultMonth} components={CALENDAR_COMPONENTS} classNames={CALENDAR_RANGE_CLASSNAMES} className={CALENDAR_CLASS} style={CALENDAR_STYLE} />
                ) : (
                  <DayPicker mode="single" selected={single} onSelect={setSingle} defaultMonth={defaultMonth} components={CALENDAR_COMPONENTS} classNames={CALENDAR_RANGE_CLASSNAMES} className={CALENDAR_CLASS} style={CALENDAR_STYLE} />
                )}
              </div>
            </div>
          )}

          {/* tz toggle: hidden for date-only, and for in_last (which is a
              relative window resolved against 'now' — the zone doesn't move
              the result meaningfully, so showing the control would mislead). */}
          <div className={cn('mb-3 flex items-center gap-3 text-sm', (dateOnly || operator === 'in_last') && 'hidden')}>
            <span className="text-neutral-500 dark:text-neutral-400">Time zone:</span>
            {(['event', 'utc'] as Zone[]).map((z) => (
              <label key={z} className="inline-flex cursor-pointer items-center gap-1.5">
                <input type="radio" name={tzGroupName} checked={zone === z} onChange={() => setZone(z)} className="accent-blue-600" />
                <span className={cn('font-medium', zone === z ? 'text-neutral-900 dark:text-neutral-100' : 'text-neutral-500 dark:text-neutral-400')}>
                  {z === 'event' ? 'Event time' : 'UTC'}
                </span>
              </label>
            ))}
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={apply}
              className="flex-1 rounded-lg bg-blue-600 py-2 text-sm font-semibold text-white hover:bg-blue-700"
            >
              Apply
            </button>
            {hasValue && (
              <button
                type="button"
                onClick={clear}
                className="rounded-lg border border-neutral-300 px-3 py-2 text-sm text-neutral-600 hover:bg-neutral-50 dark:border-neutral-700 dark:text-neutral-300 dark:hover:bg-neutral-800"
              >
                Clear
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
